import React, { memo, useMemo } from "react";
import { Box } from "@mui/material";
import { pdfRectToCanvasRect } from "../../../../../../../utils/coordinateUtils";

const EMPTY_ITEMS = [];
const PdfTextItems = memo(function PdfTextItems({
  viewport,
  pageHeight,
  textItems,
  selectedTextItem,
  selectedExtraPdfTextItems,
  pdfTextEdits,
  selectToolActive,
  highlightFields,
  setCurrentProcessingFile,
  textSelectionMode,
  fieldToolActive,
}) {
  const selectedIds = useMemo(
    () =>
      new Set([
        selectedTextItem?.id,
        ...selectedExtraPdfTextItems.map((item) => item?.id),
      ]),
    [selectedTextItem, selectedExtraPdfTextItems],
  );
  const editsById = useMemo(() => {
    const result = new Map();
    for (const edit of pdfTextEdits) {
      // Preserve the old .find() behavior if duplicate edits exist.
      if (!result.has(edit.target_item_id))
        result.set(edit.target_item_id, edit);
    }
    return result;
  }, [pdfTextEdits]);
  const positionedItems = useMemo(
    () =>
      textItems.map((item) => ({
        item,
        rect: pdfRectToCanvasRect(item.rect, viewport || pageHeight, 1),
      })),
    [textItems, viewport, pageHeight],
  );

  return (
    <>
      {positionedItems.map(({ item, rect }) => {
        const isSelected = selectedIds.has(item.id);
        const existingEdit = editsById.get(item.id);
        return (
          <React.Fragment key={item.id}>
            <Box
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!selectToolActive || !textSelectionMode) return;
                const isModifierPressed = e.ctrlKey || e.metaKey;
                setCurrentProcessingFile((prev) => ({
                  ...prev,
                  document: {
                    ...prev.document,
                    tempFields: (() => {
                      const tempFields = prev.document.tempFields;
                      const primary = tempFields.selectedPdfTextItem;
                      const extras = tempFields.selectedExtraPdfTextItems || [];
                      if (!isModifierPressed || !primary)
                        return {
                          ...tempFields,
                          selectedFormField: null,
                          selectedExtraFields: [],
                          selectedPdfTextItem: item,
                          selectedExtraPdfTextItems: [],
                        };
                      if (primary.id === item.id) {
                        const [nextPrimary = null, ...rest] = extras;
                        return {
                          ...tempFields,
                          selectedFormField: null,
                          selectedExtraFields: [],
                          selectedPdfTextItem: nextPrimary,
                          selectedExtraPdfTextItems: rest,
                        };
                      }
                      const alreadyExtra = extras.some(
                        (selected) => selected?.id === item.id,
                      );
                      return {
                        ...tempFields,
                        selectedFormField: null,
                        selectedExtraFields: [],
                        selectedPdfTextItem: primary,
                        selectedExtraPdfTextItems: alreadyExtra
                          ? extras.filter(
                              (selected) => selected?.id !== item.id,
                            )
                          : [...extras, item],
                      };
                    })(),
                  },
                }));
              }}
              style={{
                left: rect.left,
                top: rect.top,
                width: Math.max(rect.width, 4),
                height: Math.max(rect.height, 8),
              }}
              sx={{
                position: "absolute",
                backgroundColor: isSelected
                  ? "rgba(156, 39, 176, 0.14)"
                  : highlightFields && textSelectionMode
                    ? "rgba(33, 150, 243, 0.05)"
                    : "transparent",
                outline: isSelected ? "1px dashed #9c27b0" : "none",
                cursor:
                  textSelectionMode && selectToolActive ? "pointer" : "default",
                zIndex: isSelected ? 31 : 12,
                pointerEvents: fieldToolActive ? "none" : "auto",
              }}
            />
            {existingEdit?.action === "delete" && (
              <Box
                sx={{
                  position: "absolute",
                  left: `${rect.left}px`,
                  top: `${rect.top}px`,
                  width: `${Math.max(rect.width, 4)}px`,
                  height: `${Math.max(rect.height, 8)}px`,
                  backgroundColor: "rgba(255,255,255,0.96)",
                  border: "1px dashed rgba(211, 47, 47, 0.8)",
                  zIndex: 33,
                  pointerEvents: "none",
                }}
              />
            )}
            {existingEdit?.action !== "delete" && existingEdit?.new_text
              ? (() => {
                  const editedRect = pdfRectToCanvasRect(
                    existingEdit.rect,
                    viewport || pageHeight,
                    1,
                  );
                  return (
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${editedRect.left}px`,
                        top: `${editedRect.top}px`,
                        minWidth: `${Math.max(editedRect.width, 8)}px`,
                        minHeight: `${Math.max(editedRect.height, 10)}px`,
                        backgroundColor: "rgba(255,255,255,0.96)",
                        color: "#111",
                        fontSize: "12px",
                        lineHeight: 1.2,
                        padding: "1px 2px",
                        zIndex: 34,
                        pointerEvents: "none",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {existingEdit.new_text}
                    </Box>
                  );
                })()
              : null}
          </React.Fragment>
        );
      })}
    </>
  );
});

const PdfTextOverlayLayer = ({
  currentProcessingFile,
  pageNumber,
  ...props
}) => {
  const temp = currentProcessingFile?.document?.tempFields;
  const fieldToolActive = Object.values(temp?.formFieldTools || {}).some(
    (tool) => tool?.active,
  );
  return (
    <PdfTextItems
      {...props}
      fieldToolActive={fieldToolActive}
      viewport={temp?.currentDocument?.viewport || null}
      pageHeight={temp?.currentDocument?.actualHeight || 792}
      textItems={temp?.pdfTextItemsByPage?.[pageNumber] || EMPTY_ITEMS}
      selectedTextItem={temp?.selectedPdfTextItem || null}
      selectedExtraPdfTextItems={temp?.selectedExtraPdfTextItems || EMPTY_ITEMS}
      pdfTextEdits={temp?.pdfTextEdits || EMPTY_ITEMS}
    />
  );
};

export default PdfTextOverlayLayer;
